import { addMonths, format, isValid, parse } from 'date-fns';
import { CreateBonusAssignmentInput } from 'api/gql-utils/bonusAssignment/types';
import {
  PAISE_PER_RUPEE,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_TO_PAYMENT_SOURCE,
  PAYOUT_MONTHS_AHEAD,
  PAYOUT_MONTHS_BEHIND,
} from './constants';
import { BonusFormData, BonusLibraryItem, PayoutMonthOption } from './types';

export const getSelectedPaymentStatus = (paymentStatus?: string) =>
  PAYMENT_STATUS_OPTIONS.find((option) => option.value === paymentStatus);

export const findBonusType = (bonusTypes: BonusLibraryItem[], value?: string) =>
  bonusTypes.find((bonus) => bonus.value === value);

export const PAYROLL_MONTH_FORMAT = 'yyyy-MM';

export const parsePayrollMonth = (payrollMonth?: string | null): Date | null => {
  if (!payrollMonth) return null;
  const parsed = parse(payrollMonth, PAYROLL_MONTH_FORMAT, new Date(2000, 0, 1));
  return isValid(parsed) ? parsed : null;
};

export const formatPayrollMonth = (payrollMonth?: string | null, dateFormat = 'MMM yyyy') => {
  const parsed = parsePayrollMonth(payrollMonth);
  return parsed ? format(parsed, dateFormat) : '';
};

export const toPayrollMonth = (date?: string | null) => (date ? date.slice(0, 7) : '');

export const getPayoutMonthOptions = (today: Date = new Date()): PayoutMonthOption[] => {
  const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const options: PayoutMonthOption[] = [];

  for (let offset = PAYOUT_MONTHS_AHEAD; offset >= -PAYOUT_MONTHS_BEHIND; offset -= 1) {
    const month = addMonths(thisMonth, offset);
    options.push({
      value: format(month, PAYROLL_MONTH_FORMAT),
      title: format(month, 'MMMM yyyy'),
    });
  }

  return options;
};

export const getClawbackWindowEndMonth = (
  payoutMonth?: string | null,
  clawbackPeriod?: number | string | null,
): string => {
  const parsed = parsePayrollMonth(payoutMonth);
  const months = Number(clawbackPeriod);
  if (!parsed || !clawbackPeriod || !Number.isFinite(months) || months <= 0) return '';
  return format(addMonths(parsed, months - 1), PAYROLL_MONTH_FORMAT);
};

export const getClawbackDeadline = (formData: Partial<BonusFormData>) =>
  formatPayrollMonth(
    getClawbackWindowEndMonth(formData.payoutMonth, formData.clawbackMonths),
    'MMMM yyyy',
  );

export const resolveClawbackPeriod = (
  hasClawback: boolean,
  clawbackMonths?: string | null,
): number | null => {
  if (!hasClawback) return 0;
  const months = Number(clawbackMonths);
  if (!clawbackMonths || !Number.isInteger(months) || months < 1) return null;
  return months;
};

export const parseBonusAmountInput = (raw: string): string | null => {
  const stripped = raw.replace(/[₹,\s]/g, '');
  if (stripped === '') return '';
  if (!/^\d*\.?\d*$/.test(stripped)) return null;

  return stripped.replace(/^0+(?=\d)/, '');
};

export const formatBonusAmountInput = (value: string): string => {
  if (!value) return '';
  const [whole, ...fraction] = value.split('.');
  const grouped = whole === '' ? '' : Number(whole).toLocaleString('en-IN');
  return value.includes('.') ? `${grouped}.${fraction.join('')}` : grouped;
};

export const formatBonusAmountForDisplay = (value: string): string =>
  formatBonusAmountInput(value.replace(/\.$/, ''));

export const rupeesToPaise = (rupees: number): number =>
  Math.round(Number((rupees * PAISE_PER_RUPEE).toFixed(2)));

export const buildCreateBonusAssignmentInput = (
  formData: Partial<BonusFormData>,
): CreateBonusAssignmentInput | null => {
  const hasClawback = formData.hasClawback === 'true';
  const paymentSource = formData.paymentStatus
    ? PAYMENT_STATUS_TO_PAYMENT_SOURCE[formData.paymentStatus]
    : undefined;
  const amount = Number(formData.bonusAmount);

  if (!formData.peopleId || !formData.bonusType || !formData.bonusAmount) return null;
  if (!formData.payoutMonth || !paymentSource) return null;
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const clawbackPeriod = resolveClawbackPeriod(hasClawback, formData.clawbackMonths);
  if (clawbackPeriod === null) return null;

  return {
    bonus_component_id: formData.bonusType,
    user_id: formData.peopleId,
    amount: rupeesToPaise(amount).toString(),
    payroll_month: formData.payoutMonth,
    payment_source: paymentSource,
    clawback_applicable: hasClawback,
    clawback_period: clawbackPeriod,
    remarks: formData.remark || undefined,
  };
};
